import type { Maybe, NotificationConfigObject } from '@/graphql/types';
import { DATA_TYPE, type DataTypeValue } from '../enums';

const privateNotesThresholdBlockPattern =
    /```report-builder-sound-thresholds\s*(?<json>[\s\S]*?)\s*```/;

export type SoundThresholdDataType = DATA_TYPE.LXEQ | DATA_TYPE.LXTMAX | DATA_TYPE.LXN;

const SOUND_THRESHOLD_DATA_TYPES: readonly DataTypeValue[] = [
    DATA_TYPE.LXEQ,
    DATA_TYPE.LXTMAX,
    DATA_TYPE.LXN,
];

export interface SoundThresholdLine {
    key: string;
    label: string;
    value: number;
}

interface SoundAlarm {
    key?: Maybe<string>;
    title?: Maybe<string>;
    alarmLevel?: Maybe<number>;
}

export function isSoundThresholdDataType(
    dataType: DataTypeValue
): dataType is SoundThresholdDataType {
    return SOUND_THRESHOLD_DATA_TYPES.some(
        (soundThresholdDataType) => soundThresholdDataType === dataType
    );
}

function requireFiniteAlarmLevel(alarm: SoundAlarm, dataType: DataTypeValue): number {
    if (typeof alarm.alarmLevel !== 'number' || !Number.isFinite(alarm.alarmLevel)) {
        throw new Error(`Sound threshold alarm for ${dataType} is missing a finite alarm level.`);
    }

    return alarm.alarmLevel;
}

function createThresholdLine(
    alarm: SoundAlarm,
    dataType: Extract<SoundThresholdDataType, DATA_TYPE.LXEQ | DATA_TYPE.LXTMAX>,
    index: number
): SoundThresholdLine {
    if (!alarm.key) {
        throw new Error(`Sound threshold alarm for ${dataType} is missing a key.`);
    }

    const value = requireFiniteAlarmLevel(alarm, dataType);
    const defaultTitle = (
        dataType === DATA_TYPE.LXEQ
            ? gettext('LEQ_ALARM_DEFAULT_NAME')
            : gettext('LMAX_ALARM_DEFAULT_NAME')
    ).replace('{index}', String(index + 1));

    return {
        key: alarm.key,
        label: alarm.title || defaultTitle,
        value,
    };
}

function requireString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(
            `Sound threshold private notes field '${fieldName}' must be a non-empty string.`
        );
    }

    return value;
}

function requireFiniteNumber(value: unknown, fieldName: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(
            `Sound threshold private notes field '${fieldName}' must be a finite number.`
        );
    }

    return value;
}

function parsePrivateNotesJson(privateNotes: string): unknown {
    const match = privateNotes.match(privateNotesThresholdBlockPattern);

    if (!match) {
        return null;
    }

    const json = match.groups?.json;
    if (!json) {
        throw new Error('Sound threshold private notes block is missing JSON content.');
    }

    try {
        return JSON.parse(json);
    } catch (error) {
        throw new Error('Sound threshold private notes block contains invalid JSON.', {
            cause: error,
        });
    }
}

function getLxnThresholdLinesFromPrivateNotes(
    privateNotes: string | null | undefined
): SoundThresholdLine[] {
    if (!privateNotes) {
        return [];
    }

    const parsed = parsePrivateNotesJson(privateNotes);
    if (!parsed) {
        return [];
    }

    // A present block is treated as an explicit LXN threshold config for this temporary POC
    // escape hatch. Keep the schema strict so malformed notes fail loudly in report output.
    if (typeof parsed !== 'object' || parsed === null || !('lXn' in parsed)) {
        throw new Error("Sound threshold private notes block must contain an 'lXn' array.");
    }

    const thresholds = (parsed as { lXn: unknown }).lXn;
    if (!Array.isArray(thresholds)) {
        throw new Error("Sound threshold private notes field 'lXn' must be an array.");
    }

    return thresholds.map((threshold, index) => {
        if (typeof threshold !== 'object' || threshold === null) {
            throw new Error(
                `Sound threshold private notes entry 'lXn.${index}' must be an object.`
            );
        }

        const entry = threshold as Record<string, unknown>;
        return {
            key: requireString(entry.key, `lXn.${index}.key`),
            label: requireString(entry.label, `lXn.${index}.label`),
            value: requireFiniteNumber(entry.value, `lXn.${index}.value`),
        };
    });
}

export function getSoundThresholdLines(
    notificationConfig: Pick<NotificationConfigObject, 'leqAlarms' | 'maxAlarms'>,
    dataType: DataTypeValue,
    privateNotes?: string | null
): SoundThresholdLine[] {
    if (!isSoundThresholdDataType(dataType)) {
        throw new Error(`Sound thresholds are not supported for data type '${dataType}'.`);
    }

    if (dataType === DATA_TYPE.LXN) {
        return getLxnThresholdLinesFromPrivateNotes(privateNotes);
    }

    const alarms =
        dataType === DATA_TYPE.LXEQ ? notificationConfig.leqAlarms : notificationConfig.maxAlarms;

    if (!Array.isArray(alarms)) {
        throw new Error(`Sound threshold alarms for ${dataType} are missing.`);
    }

    return alarms.map((alarm, index) => {
        if (!alarm) {
            throw new Error(`Sound threshold alarms for ${dataType} contain an empty alarm.`);
        }

        return createThresholdLine(alarm, dataType, index);
    });
}
